import { apiService } from './api';
import apiClient from './api';
import { AuthService } from './authService';
import { downloadRecommendationsExcel } from './fileDownload';
import { readNotReady } from './reportNotReady';
import { fetchReportViewHtml } from './reportViewService';
import {
  AgentPortalBatchSummary,
  AgentPortalWorkspace,
  AgentReportType,
  CreateExternalSnapshotRequest,
  ExternalRecommendationSnapshotDto,
  ExternalSnapshotListItemDto,
} from '../types/agentPortal';
import { ApiResponse, VerifyEmailResponse } from '../types';
import { RelatedWorkflowDocuments, WorkflowDocument } from '../types/workflow';
import { ROLE_EXTERNAL } from '../constants/roles';

/**
 * Portal-agnostic recommendation-snapshot client. Both the Agent and Partner services expose one of
 * these so the editable-pivot hook (useExternalRecommendationPanel) can be portal-agnostic — it never
 * needs to know which surface it is talking to. All scoping (origin/partner) is server-side, derived
 * from the route + auth, so the methods only ever carry the batch id, snapshot id, and payload.
 */
export interface SnapshotClient {
  create: (batchId: number, request: CreateExternalSnapshotRequest) => Promise<ApiResponse<ExternalRecommendationSnapshotDto>>;
  list: (batchId: number) => Promise<ApiResponse<ExternalSnapshotListItemDto[]>>;
  getActive: (batchId: number) => Promise<ApiResponse<ExternalRecommendationSnapshotDto>>;
  get: (batchId: number, snapshotId: number) => Promise<ApiResponse<ExternalRecommendationSnapshotDto>>;
  activate: (batchId: number, snapshotId: number) => Promise<ApiResponse<ExternalRecommendationSnapshotDto>>;
  delete: (batchId: number, snapshotId: number) => Promise<ApiResponse<boolean>>;
  /** Download the index-free recommendations export (.xlsx) and trigger a browser download. */
  exportRecommendations: (batchId: number, filename?: string) => Promise<void>;
}

/**
 * Client for the external-user (Agent Portal) read surface.
 *
 * Mirrors the shape of batchService but talks ONLY to /api/agent-portal — the single
 * allow-listed controller for the External role. Every other DRIS endpoint is 403'd for
 * External users by the default-deny boundary, so this service is the portal's whole API.
 *
 * Responses are exclusively the index-free AgentPortal* shapes; the server never sends indices.
 */
export const agentPortalService = {
  /** List batches the caller may see (accessible via grants AND approved). */
  getBatches: async () => {
    return apiService.get<AgentPortalBatchSummary[]>('/agent-portal/batches');
  },

  /** Index-free analysis payload for one accessible + approved batch. */
  getWorkspace: async (batchId: number) => {
    return apiService.get<AgentPortalWorkspace>(`/agent-portal/batches/${batchId}/workspace`);
  },

  // ---- related documents (view + upload only; no download/delete on the agent surface) ----
  /** Farmer-wide documents grouped by workflow, scoped to the agent's accessible + approved batches. */
  getRelatedDocuments: async (batchId: number, signal?: AbortSignal): Promise<RelatedWorkflowDocuments> => {
    const res = await apiService.get<RelatedWorkflowDocuments>(
      `/agent-portal/batches/${batchId}/related-documents`, undefined, signal);
    if (!res.success || !res.data) throw new Error(res.message || 'Failed to fetch documents');
    return res.data;
  },
  /** Upload a document; attaches to the workflow owning this batch. */
  uploadDocument: async (
    batchId: number, file: File, category: string, description: string,
    onProgress?: (progress: number) => void,
  ): Promise<WorkflowDocument> => {
    const form = new FormData();
    form.append('file', file);
    if (category) form.append('category', category);
    if (description) form.append('description', description);
    const response = await apiClient.post<ApiResponse<WorkflowDocument>>(
      `/agent-portal/batches/${batchId}/documents`, form,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || file.size)))
          : undefined,
      },
    );
    if (!response.data.success || !response.data.data) throw new Error(response.data.message || 'Failed to upload document');
    return response.data.data;
  },

  /**
   * DRI-95: the approved full report as HTML (`GET api/report-view/portal/batches/{id}`), for the workspace's report
   * view. Null on 404; any other failure throws the server's message (403 gate, 409, 429 "still being prepared").
   */
  getReportView: (batchId: number, signal?: AbortSignal): Promise<string | null> =>
    fetchReportViewHtml(`/report-view/portal/batches/${batchId}`, signal),

  /**
   * Download an agent-safe report PDF as a Blob.
   *
   * The body is produced by the dedicated AgentSafeReportService (T3). Until that lands the
   * endpoint returns 501; callers should surface a "not yet available" message in that case.
   *
   * DRI-19: a soil report being updated answers 409; `validateStatus` lets it resolve so its Blob
   * body can be read, and `readNotReady` throws `ReportNotReadyError` with the server's message.
   * 401 refresh and every other status go through the interceptor as before.
   */
  downloadReportBlob: async (batchId: number, type: AgentReportType): Promise<Blob> => {
    const response = await apiClient.get(
      `/agent-portal/batches/${batchId}/reports/${type}`,
      {
        responseType: 'blob',
        timeout: 300000,
        validateStatus: (s: number) => (s >= 200 && s < 300) || s === 409,
      },
    );
    await readNotReady(response);
    return response.data as Blob;
  },

  /**
   * Tenant-scoped editable-recommendation snapshot client for the Agent portal. The server scopes
   * every operation to (batch, Origin.Agent, partnerId:null) from the route + auth — these working
   * copies never touch the internal approved artifact or workflow approval state.
   */
  snapshotClient: {
    create: (batchId, request) =>
      apiService.post<ExternalRecommendationSnapshotDto>(
        `/agent-portal/batches/${batchId}/recommendation-snapshots`, request,
      ),
    list: (batchId) =>
      apiService.get<ExternalSnapshotListItemDto[]>(
        `/agent-portal/batches/${batchId}/recommendation-snapshots`,
      ),
    getActive: (batchId) =>
      apiService.get<ExternalRecommendationSnapshotDto>(
        `/agent-portal/batches/${batchId}/recommendation-snapshots/active`,
      ),
    get: (batchId, snapshotId) =>
      apiService.get<ExternalRecommendationSnapshotDto>(
        `/agent-portal/batches/${batchId}/recommendation-snapshots/${snapshotId}`,
      ),
    activate: (batchId, snapshotId) =>
      apiService.put<ExternalRecommendationSnapshotDto>(
        `/agent-portal/batches/${batchId}/recommendation-snapshots/${snapshotId}/activate`,
      ),
    delete: (batchId, snapshotId) =>
      apiService.delete<boolean>(
        `/agent-portal/batches/${batchId}/recommendation-snapshots/${snapshotId}`,
      ),
    exportRecommendations: (batchId, filename) =>
      downloadRecommendationsExcel(`/agent-portal/batches/${batchId}/recommendations/export`, batchId, filename),
  } satisfies SnapshotClient,
};

/**
 * Public (anonymous) self-registration for external users. Lives outside the auth shell —
 * does not require a token. The backend always responds with a neutral message (it never
 * reveals whether the email already exists).
 */
export const agentRegistrationService = {
  register: async (request: {
    email: string;
    firstName: string;
    lastName: string;
    password: string;
    /** Optional contact number, persisted to User.Phone (registration-gated intake pivot). */
    phone?: string;
  }) => {
    return apiService.post<boolean>('/auth/register', request);
  },

  /**
   * Confirm a registration email using the token from the verification link. `data.signedIn` is true
   * only when this call activated the account (the server then also set the refresh cookie).
   */
  verifyEmail: async (token: string) => {
    return apiService.post<VerifyEmailResponse>('/auth/verify-email', { token });
  },

  /**
   * Confirm a registration email with the emailed 6-digit code. Same contract as verifyEmail: a
   * neutral `signedIn: false` for every failure (wrong/expired/locked code, unknown address) — never
   * show per-cause copy.
   */
  verifyEmailCode: async (email: string, code: string) => {
    return apiService.post<VerifyEmailResponse>('/auth/verify-email-code', { email, code });
  },

  /**
   * Request a fresh verification link for an address whose 24h link lapsed. Enumeration-safe:
   * the backend returns one neutral message for unknown, verified and unverified addresses alike,
   * so callers must NEVER branch their UI on the response — always show the same confirmation.
   */
  resendVerification: async (email: string) => {
    return apiService.post<boolean>('/auth/resend-verification', { email });
  },
};

/**
 * Convenience guard used by routing/UI: is the currently-authenticated user an External user?
 * Reads from the persisted user info (same source AuthContext initializes from).
 */
export function isExternalUser(): boolean {
  const user = AuthService.getUser();
  return user?.roles?.includes(ROLE_EXTERNAL) ?? false;
}
